import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiGrid, FiBookOpen, FiCpu } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

/* ─── Ícono Meta (SVG inline del logo ∞) ─── */
const MetaIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="currentColor">
    <path d="M8 24c0-4.2 1.6-7.8 4-10 1.7-1.5 3.6-2.2 5.4-2.2 2.2 0 4.2 1 6.2 3.1.9 1 1.9 2.3 3 4 .7 1 1.4 2.2 2.2 3.5.6-1 1.2-1.9 1.8-2.8 2.6-3.9 5.2-5.8 8-5.8 2.2 0 4.3.9 6 2.7 2 2.1 3.4 5.3 3.4 9.5 0 2.5-.5 4.8-1.4 6.6-.7 1.4-1.7 2.6-2.9 3.4-1 .7-2.1 1-3.1 1-1.6 0-3.1-.7-4.6-2.1-.8-.7-1.6-1.6-2.5-2.8l-2.2-3.1-2.2 3c-.9 1.3-1.8 2.3-2.6 3-1.4 1.3-2.9 2-4.5 2-1.2 0-2.3-.4-3.3-1.1-1.2-.9-2.2-2.2-2.9-3.8C8.5 28.5 8 26.4 8 24zm8-8.8c-1.1 0-2.3.5-3.3 1.5-1.7 1.6-2.9 4.4-2.9 7.3 0 1.8.4 3.5 1 4.8.5 1.1 1.1 1.9 1.8 2.4.5.4 1 .5 1.6.5 1 0 2-.5 3.1-1.6.6-.6 1.3-1.4 2.1-2.6l2.5-3.5-1.1-1.7c-1-1.5-1.8-2.6-2.5-3.4-1.2-1.3-2.2-1.7-3.3-1.7zm16.3 0c-1.9 0-3.8 1.6-5.9 4.8-.5.8-1 1.6-1.6 2.6l1.6 2.3c.9 1.4 1.7 2.5 2.4 3.3 1.4 1.6 2.7 2.4 4 2.4.6 0 1.2-.2 1.8-.6.8-.6 1.5-1.5 2-2.7.6-1.3.9-3 .9-4.8 0-3.1-1-5.6-2.6-7-1-1-2-1.3-2.6-1.3z"/>
  </svg>
);

/* ─── Ícono Cursos / Artículos de venta ─── */
const CursosIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 01-8 0"/>
  </svg>
);

/* ─── Badge "Nuevo" con glow pulsante ─── */
const NuevoBadge = ({ color, glow }) => (
  <div style={{ position: "relative", flexShrink: 0 }}>
    <motion.div
      animate={{ opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      style={{
        position: "absolute", inset: -3, borderRadius: 999,
        pointerEvents: "none", boxShadow: `0 0 10px 3px ${glow}`,
      }}
    />
    <div style={{
      display: "flex", alignItems: "center", gap: 5,
      padding: "3px 9px 3px 7px", borderRadius: 999,
      background: `linear-gradient(135deg, ${color}20 0%, ${color}0a 100%)`,
      border: `1px solid ${color}55`,
    }}>
      <motion.div
        animate={{ scale: [1, 1.6, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        style={{
          width: 5, height: 5, borderRadius: "50%",
          background: color, boxShadow: `0 0 6px 2px ${glow}`,
        }}
      />
      <span style={{
        fontSize: 8, fontWeight: 800, letterSpacing: "0.22em",
        textTransform: "uppercase", color, lineHeight: 1,
      }}>
        Nuevo
      </span>
    </div>
  </div>
);

/* ═══════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════ */
const FloatingAdminMenu = ({ showMenu, setShowMenu }) => {
  const navigate = useNavigate();

  const menuItems = [
    {
      title: "Administrar Mentorías",
      icon: <FiBookOpen size={18} />,
      path: "/admin-mentorias",
      isNew: false,
    },
    {
      title: "Administrar Agente IA",
      icon: <FiCpu size={18} />,
      path: "/admin-ia",
      isNew: false,
    },
    {
      title: "Administrar Meta Ads",
      icon: <MetaIcon size={18} />,
      path: "/admin-meta",
      isNew: true,
      accentColor: "#0082FB",
      accentGlow: "rgba(0,130,251,0.4)",
    },
    {
      title: "Administrar Cursos",
      icon: <CursosIcon size={18} />,
      path: "/admin-cursos",
      isNew: true,
      accentColor: "#C9A84C",
      accentGlow: "rgba(201,168,76,0.4)",
    },
  ];

  return (
    <>
      <style>{`
        @keyframes float-idle {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-4px); }
        }
        .menu-float-btn { animation: float-idle 3.5s ease-in-out infinite; }
        .menu-float-btn:hover { animation: none; }
      `}</style>

      {/* ── BOTÓN FLOTANTE ── */}
      <motion.button
        whileTap={{ scale: 0.88 }}
        whileHover={{ scale: 1.08 }}
        onClick={() => setShowMenu(!showMenu)}
        className="menu-float-btn fixed bottom-6 left-6 z-[9999] p-4 rounded-2xl backdrop-blur-xl"
        style={{
          background: "linear-gradient(135deg, #1a1510 0%, #0f0d0a 100%)",
          border: "1px solid rgba(184,151,127,0.35)",
          color: "#C9A84C",
          boxShadow: "0 0 24px rgba(201,168,76,0.12), 0 8px 32px rgba(0,0,0,0.55)",
        }}
      >
        <motion.div
          animate={{ rotate: showMenu ? 45 : 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <FiGrid size={22} />
        </motion.div>

        {!showMenu && (
          <motion.div
            animate={{ scale: [1, 1.7, 1], opacity: [0.35, 0, 0.35] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-[-5px] rounded-[20px] pointer-events-none"
            style={{ border: "1px solid rgba(201,168,76,0.28)" }}
          />
        )}
      </motion.button>

      {/* ── MENÚ ── */}
      <AnimatePresence>
        {showMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[9998]"
              style={{ background: "rgba(0,0,0,0.2)", backdropFilter: "blur(2px)" }}
              onClick={() => setShowMenu(false)}
            />

            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.94 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="fixed bottom-24 left-6 z-[9999] w-[290px] rounded-3xl p-3 backdrop-blur-xl overflow-hidden"
              style={{
                background: "linear-gradient(160deg, #161210 0%, #0d0b09 100%)",
                border: "1px solid rgba(184,151,127,0.18)",
                boxShadow: "0 24px 60px rgba(0,0,0,0.65), inset 0 0 0 1px rgba(201,168,76,0.05)",
              }}
            >
              {/* Top shimmer */}
              <div className="absolute top-0 left-5 right-5 h-px"
                style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent)" }}
              />

              {/* Label */}
              <div className="px-3 pt-1 pb-2">
                <span className="text-[9px] font-bold tracking-[0.22em] uppercase"
                  style={{ color: "rgba(201,168,76,0.35)" }}>
                  Panel de administración
                </span>
              </div>

              <div className="flex flex-col gap-1">
                {menuItems.map((item, index) => (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.06, duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => { navigate(item.path); setShowMenu(false); }}
                    className="flex items-center gap-3 w-full px-3 py-3 rounded-2xl border border-transparent transition-all duration-200"
                    style={{ color: item.isNew ? item.accentColor : "#B8977F" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = item.isNew
                        ? `${item.accentColor}12`
                        : "rgba(184,151,127,0.07)";
                      e.currentTarget.style.borderColor = item.isNew
                        ? `${item.accentColor}30`
                        : "rgba(184,151,127,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.borderColor = "transparent";
                    }}
                  >
                    {/* Icon box */}
                    <div
                      className="flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{
                        width: 34, height: 34,
                        background: item.isNew ? `${item.accentColor}15` : "rgba(184,151,127,0.08)",
                        border: `1px solid ${item.isNew ? `${item.accentColor}35` : "rgba(184,151,127,0.15)"}`,
                        color: item.isNew ? item.accentColor : "#B8977F",
                      }}
                    >
                      {item.icon}
                    </div>

                    {/* Title */}
                    <span className="text-sm font-semibold tracking-wide flex-1 text-left"
                      style={{ color: item.isNew ? item.accentColor : "#c4a98a" }}>
                      {item.title}
                    </span>

                    {/* Badge Nuevo */}
                    {item.isNew && (
                      <NuevoBadge color={item.accentColor} glow={item.accentGlow} />
                    )}
                  </motion.button>
                ))}
              </div>

              {/* Bottom shimmer */}
              <div className="absolute bottom-0 left-5 right-5 h-px"
                style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.1), transparent)" }}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingAdminMenu;